import React from 'react';
import { FileText } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] mb-4">
            <FileText className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Terms of Service
          </h1>
          <p className="text-lg text-[#5B6B7F]">
            Last updated: 2025-11-18
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* 1. Definitions */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">1. Definitions</h2>
            <div className="text-[#5B6B7F] leading-relaxed space-y-3">
              <p>
                <span className="font-semibold">"Coinexus" / "the Company":</span> Coinexus Sp. z o.o., a Poland-registered Virtual Asset Service Provider (VASP).
              </p>
              <p>
                <span className="font-semibold">"Client":</span> Any approved institutional, corporate, or professional user who has passed full KYC/AML verification.
              </p>
              <p>
                <span className="font-semibold">"OTC Services":</span> Bilateral crypto-to-fiat or fiat-to-crypto exchange transactions.
              </p>
              <p>
                <span className="font-semibold">"Virtual Assets":</span> Digital representations of value as defined under EU and Polish regulations.
              </p>
              <p>
                <span className="font-semibold">"AML/CTF":</span> Anti-Money Laundering and Counter-Terrorist Financing regulations, including EU 5AMLD/6AMLD and the Polish AML Act.
              </p>
            </div>
          </div>

          {/* 2. Company Information */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">2. Company Information</h2>
            <div className="text-[#5B6B7F] leading-relaxed space-y-2">
              <p className="font-semibold">Coinexus Sp. z o.o.</p>
              <p>
                Juliana Tuwima 48, unit 11,<br />
                90-021 Łódź, Poland
              </p>
              <p>
                <span className="font-semibold">KRS:</span> 0001142506<br />
                <span className="font-semibold">NIP:</span> 7252350358<br />
                <span className="font-semibold">REGON:</span> 540348512
              </p>
              <p className="mt-4">
                The Company is a regulated VASP supervised by:<br />
                General Inspector of Financial Information (GIIF/GIFI), Poland.
              </p>
            </div>
          </div>

          {/* 3. Scope of Services */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">3. Scope of Services</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus offers OTC exchange services exclusively to verified institutional and corporate clients:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Crypto-to-fiat and fiat-to-crypto OTC transactions</li>
              <li>Bank transfers only to accounts held in the client's name</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Coinexus does not provide:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Retail exchange services</li>
              <li>Wallets or custody</li>
              <li>Trading platforms</li>
              <li>Investment products or advisory</li>
              <li>Payment services</li>
              <li>Automated trading tools</li>
            </ul>
          </div>

          {/* 4. Eligibility */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">4. Eligibility</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Service is limited strictly to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Fully verified clients</li>
              <li>Professional or institutional profiles</li>
              <li>Non-sanctioned individuals/entities</li>
              <li>Clients outside prohibited jurisdictions</li>
              <li>Clients who pass Source-of-Funds / Source-of-Wealth checks</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Minors and anonymous users are not accepted.
            </p>
          </div>

          {/* 5. AML/KYC Requirements */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">5. AML/KYC Requirements</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              As a regulated VASP, Coinexus conducts:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Government-issued ID verification</li>
              <li>Proof of address (not older than 3 months)</li>
              <li>Sanctions & PEP screening (Allpass.ai)</li>
              <li>Source-of-Wealth verification where required</li>
              <li>Enhanced Due Diligence for high-risk profiles</li>
              <li>Ongoing monitoring of client activity</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Coinexus may decline or suspend service to comply with AML/CTF obligations.
            </p>
          </div>

          {/* 6. Prohibited Activities */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">6. Prohibited Activities</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Clients may not use Coinexus services for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Money laundering or terrorism financing</li>
              <li>Sanctions evasion</li>
              <li>Fraud, financial crime, or illicit transactions</li>
              <li>Darknet-related activity</li>
              <li>Mixing/tumbling services</li>
              <li>Unlicensed gambling</li>
              <li>Shell-banking or fraudulent identities</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Suspicious activity may be reported to GIIF.
            </p>
          </div>

          {/* 7. Transaction Execution */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">7. Transaction Execution</h2>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Transactions are executed only after compliance approval.</li>
              <li>Timing depends on banking institutions and blockchain networks.</li>
              <li>Coinexus is not responsible for delays caused by third-party providers.</li>
            </ul>
          </div>

          {/* 8. Refund Policy */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">8. Refund Policy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus operates exclusively as an OTC (Over-the-counter) crypto–fiat exchange service.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              All transactions are executed only after the client's explicit confirmation.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              Refunds are not applicable once a transaction is completed, as crypto-asset transfers are irreversible.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              However, in the event of:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>a technical error on our side,</li>
              <li>an incorrect amount received due to a proven system failure,</li>
              <li>or a duplicate transaction,</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Coinexus will review the case and, if applicable, process a refund according to internal AML/CTF and compliance procedures.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              Any refund (if approved) will be sent only to the verified bank account or wallet belonging to the same client.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              For questions or assistance, please contact: <a href="mailto:support@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">support@coinexus.vip</a>
            </p>
          </div>

          {/* 9. Liability */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">9. Liability</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Services are provided "as-is."<br />
              Coinexus is not liable for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Indirect or consequential losses</li>
              <li>Market volatility</li>
              <li>Blockchain network congestion</li>
              <li>Banking system delays</li>
              <li>Failures of third-party providers</li>
            </ul>
          </div>

          {/* 10. Data Protection (GDPR Compliance) */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">10. Data Protection (GDPR Compliance)</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus processes personal data solely for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Regulatory compliance</li>
              <li>AML/KYC obligations</li>
              <li>Contractual execution</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Data retention: minimum 5 years (mandatory under AML law).<br />
              Clients may exercise GDPR rights insofar as they do not conflict with AML regulation.
            </p>
          </div>

          {/* 11. Professional Client Acquisition Policy */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">11. Professional Client Acquisition Policy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus does not conduct:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Public advertising</li>
              <li>Retail marketing</li>
              <li>Social-media campaigns</li>
              <li>Mass client solicitation</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Clients are onboarded only via direct introductions, professional referrals, and institutional networks.
            </p>
          </div>

          {/* 12. Law & Jurisdiction */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">12. Law & Jurisdiction</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              These Terms are governed by Polish law.<br />
              All disputes shall be resolved by the competent courts in Poland.
            </p>
          </div>

          {/* 13. Complaints */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">13. Complaints</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Email: <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a><br />
              Acknowledgment: 2 business days<br />
              Resolution: up to 14 business days.
            </p>
          </div>

          {/* 14. Contact */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">14. Contact</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              <a href="mailto:info@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">info@coinexus.vip</a><br />
              <a href="mailto:support@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">support@coinexus.vip</a><br />
              <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a>
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Terms;